/**
 * Core types of the rules engine. Everything here is plain JSON-serializable data.
 * Rule IDs (R-…) refer to docs/rules.md.
 */
import type { Reason } from './reasons.ts';

export const SIDES = ['red', 'blue'] as const;
export type Side = (typeof SIDES)[number];

export const DIRECTIONS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'] as const;
export type Direction = (typeof DIRECTIONS)[number];

export const WEATHERS = ['fresh', 'calm', 'storm'] as const;
export type Weather = (typeof WEATHERS)[number];

export const CELL_TYPES = ['sea', 'coast', 'land', 'reef', 'port', 'royal_port'] as const;
export type CellType = (typeof CELL_TYPES)[number];

export const SHIP_CLASS_IDS = [
  'first_rate',
  'ship_of_line',
  'sail_frigate',
  'brig',
  'cutter',
  'galleon',
  'galley',
  'ironclad_battery',
  'steam_frigate',
  'steam_corvette',
  'paddle_steamer',
  'monitor',
] as const;
export type ShipClassId = (typeof SHIP_CLASS_IDS)[number];

export const FORT_CLASS_IDS = ['first_line', 'second_line'] as const;
export type FortClassId = (typeof FORT_CLASS_IDS)[number];

export type Propulsion = 'sail' | 'oars' | 'steam';

/** R-FLEET-01 */
export interface ShipClass {
  readonly name: string;
  readonly propulsion: Propulsion;
  readonly freshSpeed: number;
  /** 0 = cannot move in calm. */
  readonly calmSpeed: number;
  readonly stormDrift: number;
  readonly shotsPerTurn: number;
  readonly range: number;
  readonly sinksAt: number;
}

/** Ships per class in one side's fleet; a missing class is 0 (R-FLEET-01, R-SIZE-*). */
export type Fleet = Readonly<Partial<Record<ShipClassId, number>>>;

/**
 * R-SETUP-02 quotas: ships placed in each first-line port, in each second-line port, and in the
 * one second-line port picked by `largePortAssignment`.
 */
export interface Deployment {
  readonly firstLine: number;
  readonly secondLine: number;
  readonly largePort: number;
}

/** R-FLEET-02 */
export interface FortClass {
  readonly name: string;
  readonly shotsPerTurn: number;
  readonly range: number;
  readonly destroyedAt: number;
}

/** Cell coordinate in "A1" notation (R-MAP-02). */
export type CellId = string;

// ---------------------------------------------------------------------------
// Map
// ---------------------------------------------------------------------------

export interface MapFort {
  readonly id: string;
  readonly side: Side;
  readonly class: FortClassId;
  /** The land cell the fort stands on (R-MAP-05). */
  readonly cell: CellId;
  readonly portCells: readonly CellId[];
}

/**
 * Map document (R-MAP-08). `rows` is one string per row, one character per cell:
 * `.` sea, `,` coast, `#` land, `*` reef, `p` port, `R` royal port, `F` fort (a land cell).
 */
export interface MapDef {
  readonly id: string;
  readonly name: string;
  readonly width: number;
  readonly height: number;
  readonly rows: readonly string[];
  readonly forts: readonly MapFort[];
  readonly royalPorts: Readonly<Partial<Record<Side, CellId>>>;
}

// ---------------------------------------------------------------------------
// Ruleset (§16)
// ---------------------------------------------------------------------------

export interface Ruleset {
  readonly hitChance: number;
  readonly oarsAndSteamUseBest: boolean;
  readonly passThroughShips: boolean;
  readonly landBlocksFire: boolean;
  readonly largePortAssignment: 'random' | Readonly<Record<Side, string>>;
  readonly firstSide: 'lot' | Side;
  readonly diagonalSqueeze: boolean;
  readonly volleyMinShooters: number;
  readonly portShipLimit: number;
  readonly fortRepairCrew: number;
  readonly fortRepairPerTurn: number;
  readonly royalPortHoldTurns: number;
  /** Each side's fleet (R-FLEET-01); both sides get the same. */
  readonly fleet: Fleet;
  readonly deployment: Deployment;
  readonly shipClasses: Readonly<Record<ShipClassId, ShipClass>>;
  readonly fortClasses: Readonly<Record<FortClassId, FortClass>>;
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export interface Player {
  readonly id: string;
  readonly name: string;
}

export interface ShipState {
  readonly id: string;
  readonly side: Side;
  readonly cls: ShipClassId;
  /** Last cell; kept after sinking for history. */
  readonly cell: CellId;
  readonly damage: number;
  readonly sunk: boolean;
  readonly shotsLeft: number;
  /** Made its one move this turn (wind, storm drift or in-port). R-TURN-06 */
  readonly moved: boolean;
  readonly inGroup: boolean;
  readonly fired: boolean;
  /** Fired while standing on an active port cell this turn. R-PORT-04 */
  readonly firedInPort: boolean;
  /** Turn number on which it entered dock, or null if not docked. R-SREP-* */
  readonly dockedOnTurn: number | null;
  /** Fort id this ship is repairing, or null. R-FREP-* */
  readonly repairingFort: string | null;
}

export interface FortRepair {
  readonly side: Side;
  readonly crew: readonly string[];
  readonly startedOnTurn: number;
}

export interface FortState {
  readonly id: string;
  readonly cls: FortClassId;
  /** Current flag (R-PORT-01). Meaningless while destroyed. */
  readonly side: Side;
  readonly cell: CellId;
  readonly portCells: readonly CellId[];
  readonly hits: number;
  readonly destroyed: boolean;
  readonly shotsLeft: number;
  readonly fired: boolean;
  readonly repair: FortRepair | null;
}

export interface SpinResult {
  readonly direction: Direction;
  readonly weather: Weather;
}

export type TurnPhase = 'open' | 'declared' | 'spun';

export interface TurnState {
  readonly number: number;
  readonly side: Side;
  readonly phase: TurnPhase;
  readonly group: readonly string[];
  readonly spin: SpinResult | null;
  /** Ships of the active side on the enemy Royal Port at turn start. R-ROYAL-04 */
  readonly royalAtStart: readonly string[];
}

export type GameStatus =
  | { readonly kind: 'playing' }
  | {
      readonly kind: 'won';
      readonly winner: Side;
      readonly reason: 'royal_port' | 'annihilation' | 'resignation';
    }
  | { readonly kind: 'draw' };

export interface GameState {
  readonly rulesVersion: string;
  readonly map: MapDef;
  readonly ruleset: Ruleset;
  readonly seed: number;
  readonly players: Readonly<Record<Side, Player>>;
  /** xoshiro128** state (R-RNG-01). */
  readonly rng: readonly [number, number, number, number];
  readonly ships: readonly ShipState[];
  readonly forts: readonly FortState[];
  readonly turn: TurnState;
  readonly holdCount: Readonly<Record<Side, number>>;
  readonly drawOffer: { readonly by: Side; readonly onTurn: number } | null;
  readonly status: GameStatus;
  /** Number of actions applied so far. */
  readonly actionCount: number;
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export interface VolleyShooter {
  readonly id: string;
  readonly shots: number;
}

export type Action =
  | { readonly type: 'declareMoveGroup'; readonly side: Side; readonly shipIds: readonly string[] }
  | { readonly type: 'spinForMovement'; readonly side: Side }
  | { readonly type: 'moveShip'; readonly side: Side; readonly shipId: string; readonly to: CellId }
  | {
      readonly type: 'moveInPort';
      readonly side: Side;
      readonly shipId: string;
      readonly to: CellId;
    }
  | {
      readonly type: 'fire';
      readonly side: Side;
      readonly shooterId: string;
      readonly targetId: string;
    }
  | {
      readonly type: 'volley';
      readonly side: Side;
      readonly shooters: readonly VolleyShooter[];
      readonly targetId: string;
    }
  | { readonly type: 'enterPortRepair'; readonly side: Side; readonly shipId: string }
  | {
      readonly type: 'startFortRepair';
      readonly side: Side;
      readonly fortId: string;
      readonly crew: readonly string[];
    }
  | { readonly type: 'stopFortRepair'; readonly side: Side; readonly fortId: string }
  | { readonly type: 'endTurn'; readonly side: Side }
  | { readonly type: 'resign'; readonly side: Side }
  | { readonly type: 'offerDraw'; readonly side: Side }
  | { readonly type: 'acceptDraw'; readonly side: Side };

export type ActionType = Action['type'];

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

export type GameEvent =
  | { readonly type: 'turnStarted'; readonly turn: number; readonly side: Side }
  | { readonly type: 'groupDeclared'; readonly side: Side; readonly shipIds: readonly string[] }
  | { readonly type: 'spun'; readonly side: Side; readonly result: SpinResult }
  | {
      readonly type: 'shipMoved';
      readonly shipId: string;
      readonly from: CellId;
      readonly to: CellId;
      readonly mode: 'wind' | 'calm' | 'port' | 'storm';
    }
  | { readonly type: 'shipAnchored'; readonly shipId: string; readonly cell: CellId }
  | { readonly type: 'shipWrecked'; readonly shipId: string; readonly cell: CellId }
  | {
      readonly type: 'shot';
      readonly shooters: readonly VolleyShooter[];
      readonly targetId: string;
      readonly volley: boolean;
      readonly hit: boolean;
    }
  | { readonly type: 'shipDamaged'; readonly shipId: string; readonly damage: number }
  | { readonly type: 'shipSunk'; readonly shipId: string; readonly cause: 'fire' | 'fort_destroyed' }
  | { readonly type: 'fortHit'; readonly fortId: string; readonly hits: number }
  | { readonly type: 'fortDestroyed'; readonly fortId: string }
  | { readonly type: 'dockEntered'; readonly shipId: string }
  | { readonly type: 'shipRepaired'; readonly shipId: string; readonly damage: number }
  | { readonly type: 'dockLeft'; readonly shipId: string }
  | {
      readonly type: 'fortRepairStarted';
      readonly fortId: string;
      readonly side: Side;
      readonly crew: readonly string[];
    }
  | {
      readonly type: 'fortRepairStopped';
      readonly fortId: string;
      readonly reason: 'voluntary' | 'crew_lost' | 'fort_destroyed' | 'completed';
    }
  | { readonly type: 'fortRepaired'; readonly fortId: string; readonly hits: number }
  | { readonly type: 'fortCaptured'; readonly fortId: string; readonly side: Side }
  | { readonly type: 'royalHold'; readonly side: Side; readonly count: number }
  | { readonly type: 'drawOffered'; readonly side: Side }
  | { readonly type: 'drawOfferExpired'; readonly side: Side }
  | { readonly type: 'resigned'; readonly side: Side }
  | { readonly type: 'gameEnded'; readonly status: GameStatus };

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export interface RuleViolationInfo {
  /** Rule ID from docs/rules.md, e.g. "R-MOVE-08". */
  readonly rule: string;
  /** Stable reason within the rule (reasons.ts), for human explanations. */
  readonly reason: Reason;
  /** Developer-facing English description; not for players. */
  readonly message: string;
}

export type ApplyResult =
  | { readonly ok: true; readonly state: GameState; readonly events: readonly GameEvent[] }
  | { readonly ok: false; readonly error: RuleViolationInfo };
